import { FC } from "react";
import { useTranslation } from "react-i18next";

import { Pencil } from "@/assets/icons/ds";
import { Accordion } from "@/components/Interface/Accordion/Accordion";
import { Button } from "@/components/Interface/Button/Button";
import { BillingAccountChip } from "@/components/Portal/Documents/BillingAccounts/BillingAccountChip";
import { BillingAccountSummary } from "@/components/Portal/Documents/BillingAccounts/BillingAccountSummary";
import {
  BillingAccountSubscription,
  getSubscriptions,
} from "@/components/Portal/Documents/BillingAccounts/billingAccountUtils";
import { DrAccount } from "@/services/model";

import { AssignedSubscriptions } from "./AssignedSubscriptions";
import { BillingAccountDetails } from "./BillingAccountDetails";

interface Props {
  account: DrAccount;
  onEdit: () => void;
  onMove?: (subscription: BillingAccountSubscription) => void;
  onBulkMove?: (simIds: number[]) => void;
  isSelecting?: boolean;
  onSelectingChange?: (isSelecting: boolean) => void;
}

/** One Vertragskonto: summary row, expandable to its details and Abos. */
export const BillingAccountItem: FC<Props> = ({
  account,
  onEdit,
  onMove,
  onBulkMove,
  isSelecting = false,
  onSelectingChange,
}) => {
  const { t } = useTranslation("portal", {
    keyPrefix: "user-account.billingAccounts",
  });
  const subscriptionCount =
    account.numberOfContracts ?? account.contractAlias?.length ?? 0;

  const header = (
    <BillingAccountSummary
      account={account}
      aside={
        <BillingAccountChip
          text={t("subscriptionCount", { count: subscriptionCount })}
          tone="info"
        />
      }
    />
  );

  return (
    <Accordion
      variant="list"
      header={header}
      actions={
        <Button
          dataTestid="billing-account-edit"
          variant="secondary"
          size="sm"
          icon={<Pencil />}
          ariaLabel={t("edit")}
          onClick={onEdit}
        />
      }
      toggleLabel={t("expand")}
      // Collapsing would unmount the checkboxes mid-selection.
      canOpen={!isSelecting}
      defaultOpen={false}
    >
      <div className="flex flex-col gap-4">
        <BillingAccountDetails account={account} />
        <AssignedSubscriptions
          subscriptions={getSubscriptions(account)}
          count={subscriptionCount}
          onMove={onMove}
          onBulkMove={onBulkMove}
          isSelecting={isSelecting}
          onSelectingChange={onSelectingChange}
        />
      </div>
    </Accordion>
  );
};
